import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Search, ClipboardCheck, Info,
  Pill, Scissors, BedDouble, Layers, HeartPulse, Settings, BriefcaseMedical, Stethoscope, CheckCircle2, Loader2
} from 'lucide-react';
import { db } from '../Firebase';
import { collection, onSnapshot } from 'firebase/firestore';

const categoryIcons = {
  'Medicines': Pill,
  'Surgical Disposables': Scissors,
  'IV Fluids': BriefcaseMedical,
  'ICU Motorized Beds': BedDouble,
  'ICU Manual Beds': BedDouble,
  'Bedside Lockers': Layers,
  'Stretchers': Layers,
  'Examination Tables': Layers,
  'Dialysis Chairs': HeartPulse,
  'Procedure Chairs': HeartPulse,
  'Instrument Trolleys': ClipboardCheck,
  'Operation Tables': Settings,
  'Surgical Pendants': Settings,
  'Anaesthetic Pendants': Settings,
  'OT Lights': Stethoscope,
  'MGGP Systems': Settings,
  'ICU Curtains': Layers
};

export default function Products({ onOpenEnquiry }) {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('Modular OT Solutions');
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);

  const location = useLocation();

  const scrollRef = useRef(null);
  const [isDown, setIsDown] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);

  const handleMouseDown = (e) => {
    setIsDown(true);
    setStartX(e.pageX - scrollRef.current.offsetLeft);
    setScrollLeft(scrollRef.current.scrollLeft);
  };

  const handleMouseLeave = () => {
    setIsDown(false);
  };

  const handleMouseUp = () => {
    setIsDown(false);
  };

  const handleMouseMove = (e) => {
    if (!isDown) return;
    e.preventDefault();
    const x = e.pageX - scrollRef.current.offsetLeft;
    const walk = (x - startX) * 2; // scroll multiplier
    scrollRef.current.scrollLeft = scrollLeft - walk;
  };

  useEffect(() => {
    if (location.state) {
      if (location.state.selectedProduct) {
        setSelectedProduct(location.state.selectedProduct);
      }
      if (location.state.selectedCategory) {
        setSelectedCategory(location.state.selectedCategory);
      }
    }
  }, [location.state]);

  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const categoriesRef = collection(db, 'product_categories');
    const unsubscribe = onSnapshot(categoriesRef, (snapshot) => {
      const list = snapshot.docs.map(doc => doc.data().name);
      const uniqueCats = Array.from(new Set(list));
      uniqueCats.sort((a, b) => a.localeCompare(b));
      setCategories(uniqueCats);
    }, (err) => {
      console.error("Error loading categories:", err);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const productsRef = collection(db, 'products');

    const unsubscribe = onSnapshot(productsRef, (snapshot) => {
      const list = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      const unique = [];
      const seen = new Set();
      for (const p of list) {
        if (p.name) {
          const norm = p.name.trim().toLowerCase();
          if (!seen.has(norm)) {
            seen.add(norm);
            unique.push(p);
          }
        }
      }
      setProducts(unique);
      setLoading(false);
    }, (err) => {
      console.error("Error loading products:", err);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    setIsLightboxOpen(false);
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [selectedProduct]);

  const filteredProducts = products.filter(p => {
    const matchesSearch = p.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.desc?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'All' || p.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  // Full Detailed Product Page View
  if (selectedProduct) {
    const IconComponent = categoryIcons[selectedProduct.category] || Stethoscope;
    const relatedProducts = products
      .filter(p => p.category === selectedProduct.category && p.name !== selectedProduct.name)
      .slice(0, 3);

    return (
      <div className="font-sans bg-brand-light min-h-screen pt-28 pb-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 space-y-8">

          {/* Breadcrumbs / Back button */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-white px-4 sm:px-6 py-3 sm:py-4 rounded-2xl border border-brand-silver/30 shadow-sm">
            <div className="flex flex-wrap items-center gap-1.5 text-[11px] sm:text-xs font-semibold text-gray-500 font-poppins min-w-0">
              <span className="hover:text-brand-blue cursor-pointer transition-colors shrink-0" onClick={() => setSelectedProduct(null)}>Catalog</span>
              <span className="text-gray-400">/</span>
              <span className="text-brand-blue truncate max-w-[80px] sm:max-w-[150px] md:max-w-none">{selectedProduct.category}</span>
              <span className="text-gray-400">/</span>
              <span className="text-brand-navy truncate max-w-[120px] sm:max-w-[250px] md:max-w-none">{selectedProduct.name}</span>
            </div>

            <button
              onClick={() => setSelectedProduct(null)}
              className="btn-secondary py-2 px-6 text-xs bg-brand-navy hover:bg-brand-blue text-white w-full sm:w-auto"
            >
              Back to Catalog
            </button>
          </div>

          {/* Main Product Layout */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-stretch">

            {/* Left: Graphic Presentation */}
            <div
              className="lg:col-span-5 rounded-3xl overflow-hidden shadow-xl border border-brand-silver/40 bg-white flex flex-col justify-between relative min-h-[400px] lg:min-h-auto group cursor-zoom-in"
              onClick={() => setIsLightboxOpen(true)}
            >
              {selectedProduct.img && !selectedProduct.img.startsWith('/assets/logo1.png') ? (
                <div className="absolute inset-0 w-full h-full overflow-hidden flex items-center justify-center p-6 bg-white">
                  <img
                    src={selectedProduct.img}
                    alt={selectedProduct.name}
                    className="w-full h-full object-contain transition-transform duration-700 group-hover:scale-95"
                  />
                </div>
              ) : (
                <div className="absolute inset-0 bg-gradient-to-br from-brand-navy to-brand-blue flex flex-col justify-center items-center p-12">
                  <div className="absolute inset-0 bg-grid-mesh opacity-10" />
                  <div className="w-28 h-28 rounded-3xl bg-white/10 text-brand-blue flex items-center justify-center border border-white/20 shadow-2xl">
                    <IconComponent size={56} />
                  </div>
                </div>
              )}            </div>

            {/* Right: Technical Specifications & Details */}
            <div className="lg:col-span-7 bg-white p-5 sm:p-8 lg:p-12 rounded-3xl border border-brand-silver/50 shadow-md space-y-6 flex flex-col justify-between">
              <div className="space-y-4">
                <div className="space-y-1.5">
                  <span className="text-[10px] font-bold text-brand-blue uppercase tracking-widest bg-brand-blue/10 px-2.5 py-0.5 rounded font-poppins inline-block">
                    {selectedProduct.category}
                  </span>
                  <h2 className="text-2xl sm:text-3xl font-bold font-montserrat text-brand-navy tracking-tight">{selectedProduct.name}</h2>
                </div>

                <p className="text-sm text-gray-600 font-poppins leading-relaxed">
                  {selectedProduct.desc}
                </p>

                <div className="space-y-3 pt-2">
                  <h4 className="text-xs font-bold font-montserrat uppercase tracking-wider text-brand-navy flex items-center gap-2">
                    <ClipboardCheck size={14} className="text-brand-blue" />
                    Technical Specifications
                  </h4>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {selectedProduct.specs.map((spec, idx) => (
                      <div key={idx} className="p-4 rounded-xl bg-brand-light border border-brand-silver flex gap-2 items-start hover:border-brand-blue hover:bg-white hover:shadow-md transition-all duration-300 hover:-translate-y-0.5">
                        <CheckCircle2 size={16} className="text-brand-green shrink-0 mt-0.5" />
                        <span className="text-xs text-gray-700 font-poppins font-medium">{spec}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Actions Box */}
              <div className="pt-6 border-t border-brand-silver/50 flex flex-wrap gap-4 items-center">
                <button
                  onClick={() => onOpenEnquiry(selectedProduct.name)}
                  className="btn-primary py-3 px-8 text-sm flex-grow font-poppins"
                >
                  Enquire Price Quote
                </button>

                <a
                  href={`https://wa.me/919225836006?text=Hello%20Kiran%20Distributors!%20I%20am%20inquiring%20about%20the%20${encodeURIComponent(selectedProduct.name)}.`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-black py-3 px-8 text-sm flex-grow font-poppins justify-center"
                >
                  <svg
                    viewBox="0 0 24 24"
                    className="w-5 h-5 fill-white text-white shrink-0"
                  >
                    <path d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946C.06 5.348 5.397.01 12.008.01c3.202.001 6.212 1.246 8.477 3.514 2.266 2.268 3.507 5.28 3.505 8.484-.004 6.657-5.34 11.997-11.953 11.997-2.005-.001-3.973-.502-5.724-1.455L0 24zm6.59-4.846c1.665.988 3.3 1.49 4.805 1.491 5.494 0 9.961-4.438 9.964-9.88A9.886 9.886 0 0 0 12.008 2.04c-5.502 0-9.963 4.44-9.966 9.882-.001 1.81.499 3.579 1.45 5.118L2.4 21.57l4.247-1.416zm9.845-5.511c-.269-.134-1.594-.786-1.841-.875-.246-.09-.425-.134-.604.134-.18.269-.695.875-.853 1.054-.157.18-.314.202-.583.067-.27-.134-1.138-.419-2.167-1.338-.802-.716-1.343-1.6-1.5-1.869-.158-.269-.017-.415.118-.549.121-.12.269-.314.403-.471.135-.157.18-.269.27-.449.09-.18.045-.337-.023-.471-.067-.134-.604-1.456-.827-1.995-.218-.524-.457-.453-.604-.461-.157-.008-.337-.009-.517-.009-.18 0-.471.067-.718.337-.247.269-.942.92-1.077 2.243-.135 1.323.83 2.6 1.138 2.959.309.359 1.854 2.829 4.49 3.971.628.272 1.118.434 1.503.556.631.2 1.204.171 1.657.104.505-.075 1.594-.65 1.819-1.278.224-.628.224-1.166.157-1.278-.067-.113-.247-.18-.517-.313z" />
                  </svg>
                  Quick Chat on WhatsApp
                </a>
              </div>
            </div>
          </div>

          {/* Related Products Grid */}
          {relatedProducts.length > 0 && (
            <div className="space-y-6 pt-8">
              <h3 className="text-xl font-bold font-montserrat text-brand-navy">Related Products in {selectedProduct.category}</h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                {relatedProducts.map((related, idx) => (
                  <div
                    key={idx}
                    className="bg-white rounded-2xl border border-brand-silver/30 shadow-sm hover:shadow-md transition-shadow overflow-hidden flex flex-col justify-between p-5 space-y-4"
                  >
                    <div className="space-y-1">
                      <h4 className="font-bold text-sm text-brand-navy font-montserrat tracking-tight leading-snug">{related.name}</h4>
                      <p className="text-[11px] text-gray-500 font-poppins line-clamp-2 leading-relaxed">{related.desc}</p>
                    </div>
                    <button
                      onClick={() => setSelectedProduct(related)}
                      className="w-full py-1.5 bg-brand-light hover:bg-brand-silver/50 text-brand-navy text-[11px] font-bold font-poppins rounded-lg border border-brand-silver/30 transition-colors"
                    >
                      View Specifications
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Lightbox / Zoomed Product Image Modal */}
          {isLightboxOpen && (
            <div
              className="fixed inset-0 z-[100] flex flex-col bg-black/90 backdrop-blur-md transition-opacity duration-300 animate-fade-in pt-[80px] pb-6"
              onClick={() => setIsLightboxOpen(false)}
            >
              {/* Close Button */}
              <button
                className="absolute top-[95px] right-6 text-white/70 hover:text-white transition-all p-3 rounded-full bg-white/10 hover:bg-white/20 focus:outline-none focus:ring-2 focus:ring-white/40 z-[110]"
                onClick={() => setIsLightboxOpen(false)}
                aria-label="Close image viewer"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>

              <div
                className="flex-grow flex flex-col items-center justify-center px-4"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="relative max-w-[92vw] max-h-[72vh] flex flex-col items-center justify-center">
                  <img
                    src={selectedProduct.img}
                    alt={selectedProduct.name}
                    className="max-w-full max-h-[62vh] object-contain rounded-2xl shadow-[0_25px_60px_-15px_rgba(0,0,0,0.7)] border border-white/10 select-none animate-scale-up"
                  />
                  <div className="mt-4 text-center">
                    <span className="text-[10px] font-bold text-brand-green uppercase tracking-widest bg-brand-green/20 px-2.5 py-0.5 rounded font-poppins inline-block mb-1">
                      {selectedProduct.category}
                    </span>
                    <h4 className="text-base sm:text-lg font-bold font-montserrat text-white tracking-tight">{selectedProduct.name}</h4>
                  </div>
                </div>
              </div>
            </div>
          )}

        </div>
      </div>
    );
  }

  return (
    <div className="font-sans bg-brand-light min-h-screen">

      {/* Header Banner */}
      <section className="bg-brand-navy text-white pt-28 pb-12 relative overflow-hidden grid-mesh">
        <div className="absolute inset-0 bg-brand-navy/85 z-10" />
        <div className="absolute inset-0 z-0 bg-brand-navy/40" />
        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-20 text-center space-y-4">
          <span className="text-xs font-bold uppercase tracking-widest text-brand-green font-poppins block">Catalog</span>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold font-montserrat text-white tracking-tight leading-tight">
            Our Premium Medical Catalog
          </h1>
          <p className="text-xs sm:text-sm text-brand-silver max-w-xl mx-auto font-poppins leading-relaxed">
            Explore authentic medicines, CE-certified surgical disposables, high-end ICU beds, modular OT lights, and copper gas piping arrays.
          </p>

          {/* Centered Premium Search Bar */}
          <div className="relative max-w-md mx-auto pt-4 z-30">
            <div className="relative">
              <input
                type="text"
                placeholder="Search catalog (e.g. ICU bed, LED light)..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-12 pr-4 py-3.5 bg-white/10 backdrop-blur-md border border-white/15 hover:border-red-500/40 hover:bg-white/15 text-white rounded-2xl focus:outline-none focus:ring-2 focus:ring-red-500/50 focus:border-red-500/50 transition-all duration-300 font-poppins text-xs sm:text-sm shadow-xl placeholder:text-brand-silver/50"
              />
              <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-brand-silver/60 pointer-events-none" />
            </div>
          </div>
        </div>
      </section>

      {/* Catalog Search & Grid Area */}
      <section className="py-12 max-w-7xl mx-auto px-6 lg:px-8">
        <div className="space-y-8">

          {/* Horizontal Scrollable Categories Filter */}
          <div className="w-full bg-white p-4 rounded-3xl border border-brand-silver/30 shadow-sm select-none">
            <div
              ref={scrollRef}
              onMouseDown={handleMouseDown}
              onMouseLeave={handleMouseLeave}
              onMouseUp={handleMouseUp}
              onMouseMove={handleMouseMove}
              className={`flex gap-2 overflow-x-auto pb-1 scrollbar-none items-center scroll-smooth ${isDown ? 'cursor-grabbing' : 'cursor-grab'}`}
            >
              {categories.map((cat) => (
                <button
                  key={cat}
                  onClick={() => setSelectedCategory(cat)}
                  className={`px-5 py-2.5 rounded-full text-xs font-bold font-poppins transition-all duration-300 border shrink-0 ${selectedCategory === cat
                    ? 'bg-brand-blue border-brand-blue text-white shadow-lg shadow-brand-blue/20'
                    : 'bg-brand-light border-brand-silver text-gray-600 hover:bg-brand-silver/20 hover:text-brand-navy'
                    }`}
                >
                  {cat}
                </button>
              ))}
            </div>
          </div>

          {/* Catalog Grid Area */}
          <div className="space-y-6">
            {loading ? (
              <div className="bg-white py-20 text-center rounded-2xl border border-brand-silver/30 shadow-sm flex flex-col items-center justify-center space-y-4">
                <Loader2 className="animate-spin text-brand-blue" size={40} />
                <p className="text-xs font-bold text-gray-500 font-poppins">Loading premium catalog...</p>
              </div>
            ) : filteredProducts.length === 0 ? (
              <div className="bg-white py-16 text-center rounded-2xl border border-brand-silver/30 shadow-sm">
                <Info size={40} className="text-brand-blue/30 mx-auto mb-3" />
                <h3 className="text-lg font-bold text-brand-navy font-montserrat">No products found</h3>
                <p className="text-sm text-gray-500 font-poppins mt-1">Try modifying your query or selecting another category.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
                {filteredProducts.map((prod, idx) => (
                  <div
                    key={idx}
                    className="bg-white rounded-3xl border border-brand-silver/30 shadow-sm hover:shadow-2xl hover:-translate-y-1.5 transition-all duration-300 overflow-hidden flex flex-col justify-between group"
                  >
                    <div className="h-64 bg-brand-light flex items-center justify-center relative overflow-hidden">
                      {prod.img && !prod.img.startsWith('/assets/logo1.png') ? (
                        <img
                          src={prod.img}
                          alt={prod.name}
                          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                        />
                      ) : (
                        <div className="w-14 h-14 rounded-2xl bg-brand-navy/10 text-brand-green flex items-center justify-center border border-brand-silver/40 group-hover:scale-110 transition-transform duration-500">
                          {React.createElement(categoryIcons[prod.category] || Stethoscope, { size: 28 })}
                        </div>
                      )}
                      <span className="absolute top-3 left-3 bg-brand-navy text-brand-green text-[9px] uppercase font-bold tracking-widest px-2.5 py-1 rounded shadow-md border border-white/5 font-poppins z-10">
                        {prod.category}
                      </span>
                    </div>

                    <div className="p-6 flex-grow space-y-4 flex flex-col justify-between">
                      <div className="space-y-2">
                        <h4 className="font-bold text-base text-brand-navy font-montserrat tracking-tight leading-snug group-hover:text-brand-blue transition-colors">{prod.name}</h4>
                        <p className="text-xs text-gray-500 font-poppins line-clamp-2 leading-relaxed">{prod.desc}</p>
                      </div>

                      <div className="space-y-2 pt-2 border-t border-brand-silver/50">
                        <button
                          onClick={() => setSelectedProduct(prod)}
                          className="w-full py-2 bg-brand-light hover:bg-brand-navy hover:text-white text-brand-navy text-xs font-bold font-poppins rounded-xl border border-brand-silver/30 transition-all duration-200"
                        >
                          Full Specification
                        </button>
                        <button
                          onClick={() => onOpenEnquiry(prod.name)}
                          className="w-full btn-green py-2 text-xs font-poppins justify-center"
                        >
                          Enquire Price Quote
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

    </div>
  );
}
